import { ArrowLeft, ArrowRight, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { useScenarios } from '@/hooks/useScenarios'
import NumberField from '@/components/common/NumberField'
import FloatingPanel from './FloatingPanel'
import { ENABLE_CARGO_TOGGLE } from '@/config/scenarioConfig'

interface RouteEditMarkPanelProps {
  s: ReturnType<typeof useScenarios>
  pos: { x: number; y: number }
  onMove: (pos: { x: number; y: number }) => void
}

// 選択マークの編集フローティングパネル（マップ右側）。
// 座標 / 向き / 走行方向 / 端点の荷役設定を担う。× で選択解除。
export default function RouteEditMarkPanel({
  s,
  pos,
  onMove,
}: RouteEditMarkPanelProps) {
  const { t } = useTranslation()
  const re = s.routeEdit
  const marks = re.marks
  const sel = re.selectedMarkIndex
  const selectedMark = sel !== null ? (marks[sel] ?? null) : null
  if (!re.isActive || sel === null || !selectedMark) return null

  const isEndpoint = sel === 0 || sel === marks.length - 1
  // 選択マークの向き（deg、-180〜180）
  const selectedThetaDeg =
    ((((re.markThetas[sel] ?? 0) * 180) / Math.PI + 540) % 360) - 180

  return (
    <FloatingPanel
      title={t('scenarios.markLabel', { index: sel + 1 })}
      pos={pos}
      onMove={onMove}
      onClose={() => re.selectMark(null)}
      width="w-64"
    >
      <div className="p-2.5 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="block text-[11px] text-gray-500 mb-0.5">
              x (m)
            </span>
            <NumberField
              value={Number(selectedMark.x.toFixed(3))}
              step={0.01}
              onCommit={(v) => re.setMarkPosition(sel, v, selectedMark.y)}
            />
          </label>
          <label className="block">
            <span className="block text-[11px] text-gray-500 mb-0.5">
              y (m)
            </span>
            <NumberField
              value={Number(selectedMark.y.toFixed(3))}
              step={0.01}
              onCommit={(v) => re.setMarkPosition(sel, selectedMark.x, v)}
            />
          </label>
        </div>

        {/* 向き。端点マークは変更可能（隣のマークが回転）。中継マークは区間方向で決まる */}
        <div>
          <span className="block text-[11px] text-gray-500 mb-0.5">
            {t('scenarios.orientation')}
          </span>
          {/* 1点のみの場合も仮の向き（draftTheta）として編集できる */}
          {isEndpoint && marks.length >= 1 ? (
            <>
              <div className="flex items-center gap-1">
                <NumberField
                  value={Number(selectedThetaDeg.toFixed(1))}
                  step={1}
                  onCommit={(v) =>
                    re.setEndpointTheta(sel, (v * Math.PI) / 180)
                  }
                />
                <span className="text-[11px] text-gray-500">°</span>
              </div>
              <div className="mt-0.5 text-[10px] text-gray-400">
                {t('scenarios.markThetaHint')}
              </div>
            </>
          ) : (
            <>
              <div className="text-xs text-gray-600 tabular-nums">
                {selectedThetaDeg.toFixed(1)}°
              </div>
              <div className="mt-0.5 text-[10px] text-gray-400">
                {t('scenarios.markThetaFixedHint')}
              </div>
            </>
          )}
        </div>

        {/* 走行方向（前のマークからこのマークへの区間）。先頭マークは対象外 */}
        {sel > 0 && (
          <div>
            <span className="block text-[11px] text-gray-500 mb-0.5">
              {t('scenarios.direction')}
            </span>
            <div className="flex gap-1.5">
              {([false, true] as const).map((reverse) => (
                <button
                  key={String(reverse)}
                  onClick={() => {
                    if (selectedMark.reverse !== reverse) re.toggleReverse(sel)
                  }}
                  className={`flex-1 flex items-center justify-center gap-1 py-1 rounded text-xs border transition-colors ${
                    selectedMark.reverse === reverse
                      ? 'border-blue-400 border-2 bg-blue-100'
                      : 'bg-white hover:bg-gray-50'
                  }`}
                >
                  {reverse ? <ArrowLeft size={13} /> : <ArrowRight size={13} />}
                  {reverse ? t('scenarios.reverse') : t('scenarios.forward')}
                </button>
              ))}
            </div>
            <div className="mt-0.5 text-[10px] text-gray-400">
              {t('scenarios.directionHint')}
            </div>
          </div>
        )}

        {/* 端点マークのみ: 荷役（高さ保存）の設定 */}
        {ENABLE_CARGO_TOGGLE && isEndpoint && (
          <div>
            <span className="block text-[11px] text-gray-500 mb-0.5">
              {t('scenarios.markKind')}
            </span>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedMark.isCargo}
                  onChange={() => re.toggleCargo(sel)}
                />
                {t('scenarios.cargoMark')}
              </label>
              {selectedMark.isCargo && (
                <div className="flex-1 flex items-center gap-1">
                  <NumberField
                    value={Math.floor(Math.max(selectedMark.height, 0) * 1000)}
                    step={10}
                    min={0}
                    onCommit={(v) => re.setMarkHeightMm(sel, v)}
                  />
                  <span className="text-[11px] text-gray-500">mm</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 選択マークの削除（2マーク未満にできない等の制約は deleteMark 側で通知） */}
        <div className="border-t pt-2">
          <button
            onClick={() => re.deleteMark(sel)}
            className="w-full flex items-center justify-center gap-1 py-1 rounded text-xs border border-red-200 text-red-600 bg-white hover:bg-red-50 transition-colors"
          >
            <Trash2 size={13} />
            {t('scenarios.deleteMark')}
          </button>
        </div>
      </div>
    </FloatingPanel>
  )
}
